import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { PinIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getOtoIndividualTripSteps } from '../../../api/enrollments/get-oto-individual-trip-steps'
import { getOtoIndividualTripStepsActions } from '../../../api/enrollments/get-oto-individual-trip-steps-actions'
import { updateOtoIndividualTripSteps } from '../../../api/enrollments/update-oto-individual-trip-steps'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import ClientOtoIndividualTripStepsForm, {
  mapOtoIndividualTripStepToInput
} from '../../../components/pages/client-space/informations/client-oto-individual-trip-steps.form'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { OtoIndividualTransferMethodEnum } from '../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

// Voyage individuel — the aller and the retour a One to One Individuel family books itself. With
// an accueil-transfert LEC needs the legs to organise the pick-up, so the parents record them
// here; with ALONE the family travels on its own end to end and only reads the information.
const ClientIndividualTripPage = () => {
  const { enrollment } = useEnrollment()
  const enrollmentId = enrollment.id
  const queryClient = useQueryClient()

  const { data: tripSteps, isPending } = useQuery({
    queryKey: ['oto-individual-trip-steps', enrollmentId],
    queryFn: () => getOtoIndividualTripSteps(enrollmentId),
    enabled: !!enrollmentId
  })

  const { data: actions } = useQuery({
    queryKey: ['oto-individual-trip-steps-actions', enrollmentId],
    queryFn: () => getOtoIndividualTripStepsActions(enrollmentId),
    enabled: !!enrollmentId
  })

  const { mutate: saveTripSteps, isPending: isSaving } = useMutation({
    mutationFn: updateOtoIndividualTripSteps,
    // the backend rejects a leg that does not land on the first day of the stay (or leave on its
    // last one) with a French message: it is shown as is
    onError: showAxiosError,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['oto-individual-trip-steps', enrollmentId] })
      queryClient.invalidateQueries({
        queryKey: ['oto-individual-trip-steps-actions', enrollmentId]
      })
      // the home progress card reads the voyage individuel from the profile completion
      queryClient.invalidateQueries({ queryKey: ['enrollment-profile-completion', enrollmentId] })
      queryClient.invalidateQueries({
        queryKey: ['enrollment-profile-completion-percentage', enrollmentId]
      })
      toast.success(
        variables.submit
          ? 'Merci, vos infos de trajet individuel ont bien été envoyées à LEC'
          : 'Vos infos de trajet individuel ont bien été enregistrées'
      )
    }
  })

  const transferMethod =
    tripSteps?.transferMethod ?? enrollment.otoIndividualProposition?.transferMethod
  const isAirportPickup = transferMethod === OtoIndividualTransferMethodEnum.AIRPORT_PICKUP

  if (isPending) return <LoadingScreen />

  return (
    <div className="flex flex-col gap-4">
      <Typography variant={'h3'} color={tailwindColors.primary.dark.DEFAULT}>
        Mes infos de trajet individuel
      </Typography>

      <Box className="bg-white-hover rounded-lg border border-grey-light flex mobile:flex-col items-start gap-2">
        <PinIcon className={'min-w-8 min-h-8 -mt-2 mobile:w-6 mobile:h-6'} />

        <div className="flex flex-col gap-3">
          <Typography variant={'secondary-body'} color={tailwindColors.grey.DEFAULT}>
            Vous avez choisi un séjour en formule 1 professeur pour 1 élève avec voyage individuel.
            Nous vous rappelons, comme indiqué dans notre documentation générale, que dans le cadre
            de ce voyage individuel, l’organisation, la réservation et le paiement du transport
            sont à votre charge. Ce voyage individuel s’effectuera donc sous votre seule
            responsabilité. Nous vous invitons à procéder dès que possible à la réservation de vos
            billets pour l’aéroport/gare d’arrivée indiquée dans votre confirmation d’inscription
            et de compléter les renseignements ci-après.
          </Typography>

          <div className="bg-red-light rounded-lg p-3">
            <Typography variant={'secondary-body'} color={tailwindColors.red.DEFAULT}>
              {isAirportPickup
                ? 'Vous avez souscrit l\'option "Accueil-transfert", vous pourrez consulter les coordonnées de la personne qui sera en charge de venir chercher votre enfant (à son arrivée) et de le déposer (à son départ) 48h avant le séjour dans l\'onglet "Carnet de voyage > Coordonnées du responsable"'
                : '« Attention : Personne ne vous attendra à l’aéroport ou à la gare à votre arrivée dans le pays d’accueil. Il relève de votre entière responsabilité de vous rendre par vos propres moyens au domicile de votre famille-hôtesse. LEC ne pourra en aucun cas intervenir en cas d’un quelconque problème qui surviendrait avant votre arrivée chez votre famille-hôtesse. »'}
            </Typography>
          </div>

          {isAirportPickup ? (
            // with an accueil-transfert the windows are reminders, listed under a separator
            <ul className="list-disc pl-4 pt-3 border-t border-grey-light flex flex-col gap-2">
              <li>
                <Typography
                  as="div"
                  className="flex flex-wrap items-center gap-1"
                  variant={'secondary-body'}
                  color={tailwindColors.grey.DEFAULT}
                >
                  L’arrivée à l’étranger doit impérativement avoir lieu un dimanche entre{' '}
                  <Badge variant="grey" size="small" text="10h00 et 18h00" className="mx-1" />
                  heure locale.
                </Typography>
              </li>
              <li>
                <Typography
                  as="div"
                  className="flex flex-wrap items-center gap-1"
                  variant={'secondary-body'}
                  color={tailwindColors.grey.DEFAULT}
                >
                  Le départ de l’étranger doit impérativement avoir lieu un samedi entre{' '}
                  <Badge variant="grey" size="small" text="10h00 et 17h00" className="mx-1" />
                  heure locale.
                </Typography>
              </li>
            </ul>
          ) : (
            <>
              <div className="bg-white-active rounded-lg p-3 border border-grey-light">
                <Typography variant={'tag'} color={tailwindColors.black.DEFAULT}>
                  L’arrivée à l’étranger doit impérativement avoir lieu un dimanche entre 10h00 et
                  18h00 heure locale.
                </Typography>
              </div>
              <div className="bg-white-active rounded-lg p-3 border border-grey-light">
                <Typography variant={'tag'} color={tailwindColors.black.DEFAULT}>
                  Le départ de l’étranger doit impérativement avoir lieu un samedi entre 10h00 et
                  17h00 heure locale.
                </Typography>
              </div>
            </>
          )}
        </div>
      </Box>

      {/* with ALONE there is no accueil-transfert to organise, so LEC does not ask for the legs */}
      {isAirportPickup && (
        <ClientOtoIndividualTripStepsForm
          disabled={!actions?.canModify || !!tripSteps?.tripStepsSubmittedAt}
          isSaving={isSaving}
          defaultValues={{
            departureTripStep: mapOtoIndividualTripStepToInput(tripSteps?.departureTripStep),
            returnTripStep: mapOtoIndividualTripStepToInput(tripSteps?.returnTripStep)
          }}
          onSave={(input) => saveTripSteps({ ...input, enrollmentId })}
        />
      )}
    </div>
  )
}

export default ClientIndividualTripPage
